import React, { useEffect, useMemo, useState } from 'react';
import { useOutletContext, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Label } from '@/components/atoms/texts/label';
import { Button } from '@/components/molecules/buttons/button';
import { useToast } from '@/components/organisms/feedback/use-toast';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { ToastAction } from '@/components/organisms/feedback/toast';
import useCondition from '@/services/admin-services/query/useCondition';
import useAddConditionVersion from '@/services/admin-services/mutation/useAddConditionVersion';
import useUpdateConditionVersion from '@/services/admin-services/mutation/useUpdateConditionVersion';
import usePublishConditionVersion from '@/services/admin-services/mutation/usePublishConditionVersion';
import HtmlWysiwygEditor from '@/components/organisms/editors/HtmlWysiwygEditor';

const normalizeHtml = (html) => (html || '')
    .replace(/>\s+</g, '><')
    .replace(/\s+/g, ' ')
    .trim();

const isManagementWebsiteDebugEnabled = () => typeof window !== 'undefined'
    && window.localStorage?.getItem('management.website.debug') === '1';

const ManagementWebsite = () => {
    const { i18n } = useTranslation('global');
    const [t] = useTranslation('global');
    const { toast } = useToast();
    const { type: selectedConditionType } = useParams();
    const {
        setActiveVersionNumber,
        setActiveVersionPublished,
        setHasUnsavedChanges,
        setAvailableVersions,
    } = useOutletContext();
    const { condition, loading, error, refetch: refetchCondition } = useCondition(selectedConditionType);
    const { addConditionVersion, loading: savingLoading } = useAddConditionVersion();
    const { updateConditionVersion, loading: updatingLoading } = useUpdateConditionVersion();
    const { publishConditionVersion, loading: publishingLoading } = usePublishConditionVersion();
    const [editorContentFr, setEditorContentFr] = useState(null);
    const [editorContentEn, setEditorContentEn] = useState(null);
    const [selectedVersionNumber, setSelectedVersionNumber] = useState(null);
    const [isEditingPublishedVersion, setIsEditingPublishedVersion] = useState(false);
    const [shouldPublishAfterCreate, setShouldPublishAfterCreate] = useState(false);
    const [isSaveConfirmOpen, setIsSaveConfirmOpen] = useState(false);
    const defaultLanguageTab = i18n.language?.toLowerCase().startsWith('fr') ? 'fr' : 'en';
    const debugEnabled = isManagementWebsiteDebugEnabled();

    const debugLog = (...args) => {
        if (!debugEnabled) {
            return;
        }

        // eslint-disable-next-line no-console
        console.log('[ManagementWebsite]', ...args);
    };

    const isSaving = savingLoading || updatingLoading || publishingLoading;

    const latestVersion = useMemo(() => {
        if (!condition?.versions?.length) {
            return null;
        }

        return [...condition.versions].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0];
    }, [condition]);

    const activeVersion = useMemo(() => {
        if (!condition?.versions?.length) {
            return null;
        }

        // Si une version est sélectionnée, la retourner
        if (selectedVersionNumber) {
            const version = condition.versions.find((v) => v.versionNumber === selectedVersionNumber);
            if (version) {
                return version;
            }
        }

        return latestVersion;
    }, [condition, latestVersion, selectedVersionNumber]);

    const isActiveVersionPublished = useMemo(() => {
        if (!activeVersion || !condition) {
            return null;
        }

        return activeVersion.versionNumber === condition.publishedVersionNumber;
    }, [activeVersion, condition]);

    const isEditorDisabled = isActiveVersionPublished && !isEditingPublishedVersion;
    const backendContentFr = activeVersion?.contentFr || '';
    const backendContentEn = activeVersion?.contentEn || '';

    // Contenu dérivé : override utilisateur si existant, sinon valeur backend (pas d'effet d'init nécessaire)
    const contentFr = editorContentFr !== null ? editorContentFr : backendContentFr;
    const contentEn = editorContentEn !== null ? editorContentEn : backendContentEn;

    const hasChanges = useMemo(
        () =>
            normalizeHtml(contentFr) !== normalizeHtml(backendContentFr)
            || normalizeHtml(contentEn) !== normalizeHtml(backendContentEn),
        [backendContentEn, backendContentFr, contentEn, contentFr],
    );

    const handleEditorContentFrChange = (nextContent) => {
        debugLog('onChange FR', {
            nextLength: (nextContent || '').length,
            backendLength: backendContentFr.length,
            sameNormalized: normalizeHtml(nextContent) === normalizeHtml(backendContentFr),
        });

        setEditorContentFr(nextContent);
    };

    const handleEditorContentEnChange = (nextContent) => {
        debugLog('onChange EN', {
            nextLength: (nextContent || '').length,
            backendLength: backendContentEn.length,
            sameNormalized: normalizeHtml(nextContent) === normalizeHtml(backendContentEn),
        });

        setEditorContentEn(nextContent);
    };

    useEffect(() => {
        setEditorContentFr(null);
        setEditorContentEn(null);
        setIsEditingPublishedVersion(false);
        setShouldPublishAfterCreate(false);

        debugLog('activeVersion changed', {
            type: selectedConditionType,
            selectedVersionNumber,
            activeVersionNumber: activeVersion?.versionNumber,
            isActiveVersionPublished,
            backendFrLength: backendContentFr.length,
            backendEnLength: backendContentEn.length,
        });
    }, [activeVersion]);

    useEffect(() => {
        debugLog('hasChanges recompute', {
            hasChanges,
            isEditorDisabled,
            frSameNormalized: normalizeHtml(contentFr) === normalizeHtml(backendContentFr),
            enSameNormalized: normalizeHtml(contentEn) === normalizeHtml(backendContentEn),
            contentFrLength: contentFr.length,
            backendFrLength: backendContentFr.length,
            contentEnLength: contentEn.length,
            backendEnLength: backendContentEn.length,
        });

        if (typeof setHasUnsavedChanges === 'function') {
            const shouldSetUnsaved = isEditorDisabled ? false : hasChanges;
            debugLog('propagate setHasUnsavedChanges', { shouldSetUnsaved });
            setHasUnsavedChanges(shouldSetUnsaved);
        }
    }, [hasChanges, isEditorDisabled, setHasUnsavedChanges]);

    useEffect(() => {
        return () => {
            if (typeof setHasUnsavedChanges === 'function') {
                setHasUnsavedChanges(false);
            }
        };
    }, [setHasUnsavedChanges]);

    useEffect(() => {
        const beforeUnloadHandler = (event) => {
            if (!hasChanges || isEditorDisabled) {
                return;
            }

            debugLog('beforeunload blocked', {
                hasChanges,
                isEditorDisabled,
                activeVersionNumber: activeVersion?.versionNumber,
            });

            event.preventDefault();
            event.returnValue = '';
        };

        window.addEventListener('beforeunload', beforeUnloadHandler);

        return () => {
            window.removeEventListener('beforeunload', beforeUnloadHandler);
        };
    }, [activeVersion, hasChanges, isEditorDisabled]);

    const isOnlyOneLanguageChanged = useMemo(() => {
        const isFrChanged = normalizeHtml(contentFr) !== normalizeHtml(backendContentFr);
        const isEnChanged = normalizeHtml(contentEn) !== normalizeHtml(backendContentEn);

        return (isFrChanged && !isEnChanged) || (!isFrChanged && isEnChanged);
    }, [backendContentEn, backendContentFr, contentEn, contentFr]);

    const handlePublishVersion = async (versionNumberToPublish) => {
        if (!versionNumberToPublish) {
            return;
        }

        try {
            await publishConditionVersion(selectedConditionType, versionNumberToPublish);

            toast({
                title: t('management.website.editor.publishSuccess'),
                description: t('management.website.editor.versionPublished'),
                variant: 'success',
            });

            await refetchCondition();
            setSelectedVersionNumber(versionNumberToPublish);
        } catch (err) {
            toast({
                title: t('management.website.editor.publishError'),
                description: err.message || t('error.fetchUnsuccessfull'),
                variant: 'error',
            });
        }
    };

    const performSave = async () => {
        try {
            if (isActiveVersionPublished && isEditingPublishedVersion) {
                const response = await addConditionVersion(selectedConditionType, contentFr, contentEn);
                const createdVersionNumber = response?.data?.addConditionVersion?.versionNumber;

                if (createdVersionNumber) {
                    setSelectedVersionNumber(createdVersionNumber);
                }

                if (shouldPublishAfterCreate && createdVersionNumber) {
                    await handlePublishVersion(createdVersionNumber);
                } else {
                    toast({
                        title: t('management.website.editor.saveSuccess'),
                        description: t('management.website.editor.versionCreatedPromptPublish'),
                        variant: 'success',
                        duration: 12000,
                        action: createdVersionNumber ? (
                            <ToastAction
                                altText={t('global.actions.publish')}
                                onClick={() => {
                                    void handlePublishVersion(createdVersionNumber);
                                }}
                            >
                                {t('global.actions.publish')}
                            </ToastAction>
                        ) : null,
                    });

                    await refetchCondition();
                }

                setIsEditingPublishedVersion(false);
                setShouldPublishAfterCreate(false);
                return;
            }

            await updateConditionVersion(
                selectedConditionType,
                selectedVersionNumber,
                contentFr,
                contentEn,
            );

            toast({
                title: t('management.website.editor.saveSuccess'),
                description: t('management.website.editor.versionUpdatedPromptPublish'),
                variant: 'success',
                duration: 12000,
                action: selectedVersionNumber ? (
                    <ToastAction
                        altText={t('global.actions.publish')}
                        onClick={() => {
                            void handlePublishVersion(selectedVersionNumber);
                        }}
                    >
                        {t('global.actions.publish')}
                    </ToastAction>
                ) : null,
            });

            await refetchCondition();
        } catch (err) {
            toast({
                title: t('management.website.editor.saveError'),
                description: err.message || t('error.fetchUnsuccessfull'),
                variant: 'error',
            });
        }
    };

    const handleSave = async () => {
        if (!hasChanges) {
            return;
        }

        if (isOnlyOneLanguageChanged) {
            setIsSaveConfirmOpen(true);
            return;
        }

        await performSave();
    };

    const handleSaveConfirmed = async () => {
        setIsSaveConfirmOpen(false);
        await performSave();
    };

    const handleCancel = () => {
        setEditorContentFr(null);
        setEditorContentEn(null);
        setIsEditingPublishedVersion(false);
        setShouldPublishAfterCreate(false);
    };

    const handleEditAsNewVersion = () => {
        setIsEditingPublishedVersion(true);
        setShouldPublishAfterCreate(false);
    };

    const handleEditAndPublish = () => {
        setIsEditingPublishedVersion(true);
        setShouldPublishAfterCreate(true);
    };

    const handlePublish = async () => {
        await handlePublishVersion(selectedVersionNumber);
    };

    useEffect(() => {
        if (typeof setAvailableVersions === 'function') {
            setAvailableVersions({
                versions: [...(condition?.versions || [])].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)),
                selectedVersionNumber,
                setSelectedVersionNumber,
                publishedVersionNumber: condition?.publishedVersionNumber,
            });
        }
    }, [condition?.versions, selectedVersionNumber, condition?.publishedVersionNumber, setAvailableVersions]);

    useEffect(() => {
        if (!condition?.versions?.length) {
            setSelectedVersionNumber(null);
            return;
        }

        const selectedExists = selectedVersionNumber !== null
            && condition.versions.some((version) => version.versionNumber === selectedVersionNumber);

        if (!selectedExists) {
            setSelectedVersionNumber(latestVersion?.versionNumber ?? null);
        }
    }, [condition?.versions, latestVersion, selectedVersionNumber]);

    useEffect(() => {
        if (typeof setActiveVersionNumber === 'function') {
            setActiveVersionNumber(activeVersion?.versionNumber ?? null);
        }

        if (typeof setActiveVersionPublished === 'function') {
            setActiveVersionPublished(isActiveVersionPublished);
        }

        return () => {
            if (typeof setActiveVersionNumber === 'function') {
                setActiveVersionNumber(null);
            }

            if (typeof setActiveVersionPublished === 'function') {
                setActiveVersionPublished(null);
            }
        };
    }, [activeVersion, isActiveVersionPublished, setActiveVersionNumber, setActiveVersionPublished]);

    return (
        <div className="flex h-full min-h-0 flex-col gap-firstDegree">
            {loading && <span className="loader" />}
            {!loading && error && <Label message="error.fetchUnsuccessfull" color="error" />}

            {!loading && !error && activeVersion && (
                <>

                    <Tabs defaultValue={defaultLanguageTab} className="flex w-full flex-1 min-h-0 flex-col bg-white rounded-sm gap-0">
                        <TabsList className="flex w-full shrink-0 justify-start  p-firstDegree pb-0 m-0 h-fit bg-transparent ">
                            <TabsTrigger value="fr" text="management.website.editor.tabs.fr" className="bg-white" />
                            <TabsTrigger value="en" text="management.website.editor.tabs.en" />
                        </TabsList>
                        <div className="flex-1 min-h-0 flex-col p-firstDegree pt-0 m-0 w-full bg-white" >
                            <TabsContent value="fr" className=" h-full flex-1 min-h-0 bg-lighter rounded-sm mt-0">
                                <HtmlWysiwygEditor
                                    titleMessage="management.website.editor.fr"
                                    value={contentFr}
                                    onChange={handleEditorContentFrChange}
                                    isDisabled={isEditorDisabled}
                                />
                            </TabsContent>

                            <TabsContent value="en" className="h-full flex-1 min-h-0 bg-lighter rounded-sm mt-0">
                                <HtmlWysiwygEditor
                                    titleMessage="management.website.editor.en"
                                    value={contentEn}
                                    onChange={handleEditorContentEnChange}
                                    isDisabled={isEditorDisabled}
                                />
                            </TabsContent>
                        </div>
                    </Tabs>

                    <div className="flex gap-secondDegree pt-firstDegree border-t">
                        {isActiveVersionPublished && !isEditingPublishedVersion && (
                            <>
                                <Button
                                    onClick={handleEditAsNewVersion}
                                    disabled={isSaving}
                                    text="management.website.editor.editAsNewVersion"
                                    showText
                                />
                                <Button
                                    onClick={handleEditAndPublish}
                                    disabled={isSaving}
                                    text="management.website.editor.editAndPublish"
                                    showText
                                />
                            </>
                        )}

                        {((isActiveVersionPublished && isEditingPublishedVersion) || !isActiveVersionPublished) && (
                            <>
                                <Button
                                    onClick={handleCancel}
                                    disabled={!hasChanges || isSaving}
                                    style="ghost"
                                    text="management.website.editor.cancel"
                                    showText
                                />
                                <Button
                                    onClick={handleSave}
                                    disabled={!hasChanges || isSaving}
                                    text="management.website.editor.save"
                                    showText
                                />
                            </>
                        )}

                        {!isActiveVersionPublished && (
                            <Button
                                onClick={handlePublish}
                                disabled={hasChanges || isSaving}
                                text="global.actions.publish"
                                showText
                            />
                        )}
                    </div>
                </>
            )}

            <Dialog open={isSaveConfirmOpen} onOpenChange={setIsSaveConfirmOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>
                            <Label message="management.website.editor.singleLanguageDialog.title" />
                        </DialogTitle>
                        <DialogDescription>
                            <Label message="management.website.editor.singleLanguageDialog.description" />
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button
                            style="ghost"
                            text="global.actions.cancel"
                            showText
                            onClick={() => setIsSaveConfirmOpen(false)}
                        />
                        <Button
                            style="fill"
                            text="management.website.editor.singleLanguageDialog.confirmSave"
                            showText
                            onClick={handleSaveConfirmed}
                        />
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default ManagementWebsite;
